/**
 * Replies that carry an enquiry's tag but which the server did not link to any RFQ.
 *
 * The per-RFQ inline panel that used to live here is gone: a supplier's correspondence now opens in
 * the docked `SupplierReplyPanel` beside the table, because rendering every reply inline is what
 * made this screen unusable once a dozen suppliers were quoting fifty lines each.
 *
 * What remains is the UNLINKED case, which genuinely belongs above the table rather than under a
 * row — the tag names the ENQUIRY, and one enquiry has several RFQs out to several suppliers, so
 * attributing one of these to a particular RFQ would be a guess. A quotation filed against the
 * wrong supplier is worse than one the purchaser has to place by hand (`D12`).
 *
 * Reading a reply is still an explicit act. `parse_supplier_reply` cannot tell a quotation from a
 * refusal: against a "Sorry no stock" mail it produced a `Supplier Quotation` holding the RFQ's
 * line at rate 0.00 and reported `matched: 1` — success, for a decline. Ask `E12` is what would
 * let it be automatic.
 */

import { Badge } from '../../components/Badge'
import { Card } from '../../components/ui'
import { useEnquiryReplies } from '../../lib/api/useRfqReplies'
import { daysAwaiting, describeAwaiting } from '../../lib/rfq'

/**
 * Replies that carry this enquiry's tag but which the server did not link to any RFQ.
 *
 * Shown once, above the table, rather than under a row — the tag names the ENQUIRY, and one
 * enquiry has several RFQs out to several suppliers, so attributing one of these to a particular
 * RFQ would be a guess. A quotation filed against the wrong supplier is worse than one the
 * purchaser has to place by hand (`D12`).
 *
 * Anything the server DID link is excluded here and appears under its own RFQ instead, where it
 * can be parsed with certainty.
 */
export function UnlinkedRepliesPanel({
  enquiryId,
  linkedIds,
  rfqChoices,
  parsingReplyId,
  onParse,
}: {
  enquiryId: string
  /** Reply ids already shown under a specific RFQ — excluded so nothing appears twice. */
  linkedIds: string[]
  /** The RFQs on this enquiry, so the purchaser can say which one a reply answers. */
  rfqChoices: Array<{ id: string; supplier: string }>
  parsingReplyId: string | null
  onParse: (rfqId: string, communication: string) => void
}) {
  const replies = useEnquiryReplies(enquiryId)
  const rows = (replies.data ?? []).filter((r) => !linkedIds.includes(r.id))
  if (rows.length === 0) return null

  return (
    <Card>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
        {rows.length} repl{rows.length === 1 ? 'y' : 'ies'} for {enquiryId} not tied to an RFQ
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
        These carry the enquiry tag but the server did not record which RFQ they answer — pick the
        supplier they came from to read one as a quotation.
      </div>
      {rows.map((reply) => (
        <div key={reply.id} style={{ borderTop: '1px solid var(--line)', padding: '10px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <b style={{ fontSize: 13 }}>{reply.sender || 'unknown sender'}</b>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              {describeAwaiting(daysAwaiting(reply.received_at, new Date()))}
            </span>
            {!reply.seen ? <Badge tone="info">unread</Badge> : null}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', margin: '3px 0 7px' }}>
            {reply.preview || reply.subject}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {rfqChoices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                className="btn sm"
                disabled={parsingReplyId === reply.id}
                onClick={() => onParse(choice.id, reply.id)}
              >
                {parsingReplyId === reply.id ? 'Reading…' : `Read as ${choice.supplier}'s quote`}
              </button>
            ))}
          </div>
        </div>
      ))}
    </Card>
  )
}
